/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2022 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
**************************************************************************/
/* eslint-disable no-underscore-dangle */

import uuid from "uuid";
import { AcrobatJS } from "@ajsprivate/acrobatjs";
import { envUtil } from "dc-core";
import { logEvent } from "../logging";
import { getAjsPath } from "../utils/ajsPathUtils";
import AwaitQueue from "./AwaitQueue";
import { MAXIMUM_ACTIVE_AJS_INSTANCE_LIMIT, acrobatjsState } from "./constants";
import { shouldEnableWasmExceptions } from "../utils/wasmUtils";

/**
 * AcrobatJSInstanceNode holds the AcrobatJS instance,
 * its init params, openPDF params
 */
class AcrobatJSInstanceNode {
    constructor(aJSInstance) {
        this._state = acrobatjsState.ACTIVE;
        this._ajsInstance = aJSInstance;
        this._ajsInstanceParameters = null;
        this._editPluginLoaded = false;
    }

    get ajsInstance() {
        return this._ajsInstance;
    }

    get state() {
        return this._state;
    }

    set state(state) {
        this._state = state;
    }

    get ajsInstanceParameters() {
        return this._ajsInstanceParameters;
    }

    set ajsInstanceParameters(ajsInstanceParameters) {
        this._ajsInstanceParameters = ajsInstanceParameters;
    }

    get editPluginLoaded() {
        return this._editPluginLoaded;
    }

    set editPluginLoaded(value) {
        this._editPluginLoaded = value;
    }

    /**
     * Terminates AcrobatJS instance if in active state.
     */
    deactivate() {
        if (this._state !== acrobatjsState.CACHED) {
            this._ajsInstance.terminate();
            this._ajsInstance = null;
            this._state = acrobatjsState.CACHED;
        }
    }

    /**
     * Cleans cache.
     * Terminates AcrobatJS instance.
     */
    terminate() {
        this._ajsInstanceParameters = null;
        this.deactivate();
    }
}

/**
 * Least recently used cache for AcrobatJSInstanceNode.
 */
export default class AcrobatJSInstanceHandler {
    constructor(options) {
        this._enableAJSLogging = options.enableAJSLogging;
        this._useAJSFullBuild = options.useAJSFullBuild;
        this._exposedAPIs = options.exposedAPIs;

        this._instanceCreationRequestCount = 0;
        this._activeNodeMap = new Map();
        this._inactiveNodeMap = new Map();
        this._workerAwaitQueue = new AwaitQueue();
        this._filesOpened = 0;
        this._instancesCreated = 0;
        this._maxActiveAJSinstanceLimit = options.maxActiveAJSinstanceLimit || MAXIMUM_ACTIVE_AJS_INSTANCE_LIMIT;
    }

    has = fileHandle => (this._activeNodeMap.has(fileHandle) || this._inactiveNodeMap.has(fileHandle));

    /**
     * @returns the number of AcrobatJS instances alive.
     */
    activeAcrobatJSInstanceCount = () => this._activeNodeMap.size;

    isFull = () => (
        this.activeAcrobatJSInstanceCount() >= this._maxActiveAJSinstanceLimit
            || this._instanceCreationRequestCount >= this._maxActiveAJSinstanceLimit
    )

    /**
     * Verifies if AcrobatJS instance corresponding to fileHandle has state.
     * @param {string} fileHandle - unique key for corresponding AcrobatJSInstanceNode.
     */
    checkInstanceState = (fileHandle, state) => {
        if (this.has(fileHandle)) {
            const acrobatJSInstanceNode = this._getAcrobatJSInstanceNode(fileHandle);
            return acrobatJSInstanceNode.state === state;
        }
        return false;
    }

    /**
     * Returns AcrobatJSInstanceNode corresponding to fileHandle.
     * @param {string} fileHandle - unique key for corresponding AcrobatJSInstanceNode.
     */
    _getAcrobatJSInstanceNode = fileHandle =>
        (this.has(fileHandle) ? (this._activeNodeMap.get(fileHandle) || this._inactiveNodeMap.get(fileHandle)) : null);

    /**
     * Returns AcrobatJS instance corresponding to fileHandle.
     * @param {string} fileHandle - unique key for corresponding AcrobatJSInstanceNode.
     */
    getAcrobatJSInstance = fileHandle => {
        if (this._activeNodeMap.has(fileHandle)) {
            const instanceNode = this._getAcrobatJSInstanceNode(fileHandle);
            // mark instance |ACTIVE|
            instanceNode.state = acrobatjsState.ACTIVE;
            return instanceNode.ajsInstance;
        }
        return null;
    }

    /**
     * Returns AcrobatJS instance params corresponding to fileHandle.
     * @param {string} fileHandle - unique key for corresponding AcrobatJSInstanceNode.
     */
    getAcrobatJSInstanceParameters = fileHandle => this._getAcrobatJSInstanceNode(fileHandle)?.ajsInstanceParameters;

    setEditPluginLoaded(fileHandle, value) {
        if (this.has(fileHandle)) {
            this._getAcrobatJSInstanceNode(fileHandle).editPluginLoaded = value;
        }
    }

    /* Utilities that handle lru cache updates. */
    /**
     * Adds worker node for fileHandle at the END of worker list.
     * @param {string} fileHandle - unique key for corresponding AcrobatJSInstanceNode.
     * @param {AcrobatJS} ajsInstance - AcrobatJS instance.
     */
    _add = (fileHandle, ajsInstance) => {
        if (this.checkInstanceState(fileHandle, acrobatjsState.CACHED)) {
            this.erase(fileHandle);
        }
        this._activeNodeMap.set(fileHandle, new AcrobatJSInstanceNode(ajsInstance));

        if (this._activeNodeMap.size > this._maxActiveAJSinstanceLimit) {
            logEvent("Active AcrobatJS instance count exceeds limit");
        }
    }

    _onAjsEventForProvider(fileHandle, evtName) {
        switch (evtName) {
        case "workerIdle":
            // Instance with edit plugin loaded should not be evicted
            if (this._getAcrobatJSInstanceNode(fileHandle).editPluginLoaded) {
                return;
            }
            this._getAcrobatJSInstanceNode(fileHandle).state = acrobatjsState.IDLE;

            if (this._workerAwaitQueue.hasPendingRequest() && this._evictAjsInstance(fileHandle)) {
                // signal cache to cache idle worker and process a pending instance request
                this._workerAwaitQueue.executeRequest();
            }
            break;
        default:
            break;
        }
    }

    /**
     * This function
     * 1) adds subscriber to AcrobatJS instance
     * 2) initializes AcrobatJS instance
     * @param {acrobatJSInstance} - AcrobatJS instance
     * @param {Object} opts - AcrobatJS instance init options
     * @private
     */
    _ajsInit(acrobatJSInstance, fileHandle, onAjsEvent) {
        acrobatJSInstance.subscribe((...args) => this._onAjsEventForProvider(fileHandle, ...args));
        if (onAjsEvent) acrobatJSInstance.subscribe(onAjsEvent);
        return acrobatJSInstance.init();
    }

    _tryEvictingAnIdleInstance() {
        const activeNodeList = Array.from(this._activeNodeMap);
        return activeNodeList.some(([fileHandle, acrobatJSInstanceNode]) => {
            if (acrobatJSInstanceNode.state === acrobatjsState.IDLE &&
                this._evictAjsInstance(fileHandle)) {
                return true;
            }
            return false;
        });
    }

    setOpenPDFParameters = (fileHandle, openPdfParams) => {
        if (this.has(fileHandle)) {
            const acrobatJSInstanceNode = this._getAcrobatJSInstanceNode(fileHandle);
            acrobatJSInstanceNode.ajsInstanceParameters = {
                ...acrobatJSInstanceNode.ajsInstanceParameters,
                openPdfParams,
            };
        }
    }

    async _getAcrobatJSWorker(config = {}) {
        const ajsConfig = {
            ajsPath: getAjsPath(),
            apiTrackingInfo: {
                enabled: true,
                trackList: this._exposedAPIs,
            },
            enableLogging: this._enableAJSLogging,
            useFullBuild: this._useAJSFullBuild,
            hostEnv: envUtil.getHostEnv(),
            enableWasmExceptions: shouldEnableWasmExceptions(),
            ...config,
        };
        return new AcrobatJS({ ...ajsConfig });
    }

    async createAcrobatJSInstance(opts = {}, instanceHandle) {
        if (this.isFull() && !this._tryEvictingAnIdleInstance()) {
            if (opts.forceEvict) {
                Array.from(this._activeNodeMap).some(([fileHandle]) => this._evictAjsInstance(fileHandle));
            } else {
                await this._workerAwaitQueue.addResourceRequest();
            }
        }

        this._instanceCreationRequestCount += 1;
        const { openPdfParams, ajsInstanceParams } = opts;
        const fileHandle = instanceHandle || uuid.v4();
        const acrobatJSInstance = await this._getAcrobatJSWorker(opts.ajsConfig);
        this._add(fileHandle, acrobatJSInstance);
        this._instanceCreationRequestCount -= 1;
        this._instancesCreated += 1;
        // cache instance params for new instances
        if (!instanceHandle) {
            this._filesOpened += 1;
            const acrobatJSInstanceNode = this._getAcrobatJSInstanceNode(fileHandle);
            acrobatJSInstanceNode.ajsInstanceParameters = { openPdfParams, ajsInstanceParams };
        }

        const instanceInitBegin = performance.now();
        return this._ajsInit(acrobatJSInstance, fileHandle, opts.onAjsEvent)
            .then(() => {
                logEvent("AcrobatJS instance created", {
                    instanceInitTime: performance.now() - instanceInitBegin,
                });
                return fileHandle;
            }).catch(err => {
                logEvent("AcrobatJS instance init error: ", err);
                this.erase(fileHandle);
                return Promise.reject(err);
            });
    }

    /**
     * Caches instance and terminates corresponding worker if it is not already |CACHED|.
     * Used to free worker resource from an alive instance.
     * @param {string} fileHandle - unique key for corresponding AcrobatJSInstanceNode.
     * @returns true if an |ACTIVE| or |IDLE| instance was cached, false otherwise.
     */
    _evictAjsInstance = fileHandle => {
        // fileHandle for which edit plugin is loaded should never be terminated.
        if (this._getAcrobatJSInstanceNode(fileHandle).editPluginLoaded ||
            this.checkInstanceState(fileHandle, acrobatjsState.CACHED)) {
            return false;
        }
        const acrobatJSInstanceNode = this._getAcrobatJSInstanceNode(fileHandle);
        this._inactiveNodeMap.set(fileHandle, acrobatJSInstanceNode);
        this._activeNodeMap.delete(fileHandle);
        acrobatJSInstanceNode.deactivate();
        return true;
    }

    /**
     * Terminates AcrobatJS instance if alive. Erases AcrobatJSInstanceNode.
     * @param {string} fileHandle - unique key for corresponding AcrobatJSInstanceNode.
     */
    erase = fileHandle => {
        const acrobatJSInstanceNode = this._getAcrobatJSInstanceNode(fileHandle);
        if (acrobatJSInstanceNode) {
            if (this.checkInstanceState(fileHandle, acrobatjsState.CACHED)) {
                this._inactiveNodeMap.delete(fileHandle);
            } else {
                this._activeNodeMap.delete(fileHandle);
            }
            acrobatJSInstanceNode.terminate();
        }

        if (!this.isFull()) {
            // execute any pending request
            this._workerAwaitQueue.executeRequest();
        }

        // when all instances are terminated, log usage stats
        if (!this._activeNodeMap.size && !this._inactiveNodeMap.size) {
            logEvent("AcrobatJS provider usage stats", {
                filesOpened: this._filesOpened,
                instancesCreated: this._instancesCreated,
            });
            // reset for logging next usage in the same session
            this._filesOpened = 0;
            this._instancesCreated = 0;
        }
    }
}
